import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { Card, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CardBackdrop from "../CardBackdrop";

const maxZoom = 12;
const minZoom = 2;
const regionSettings = {
    "north-america": { zoom: 5, center: [-91, 33] },
    europe: { zoom: 7, center: [13, 50] },
    asia: { zoom: 3, center: [84, 15] },
    "south-america": { zoom: 4, center: [-55, -20] },
    oceania: { zoom: 5, center: [140, -28] },
    africa: { zoom: 3, center: [18, -10] },
};

const PoPMarker = styled("circle")(
    ({ theme }) => ({
        "@keyframes breathe": {
            "0%": { transform: "scale(1)" },
            "45%": { transform: "scale(1.25)" },
            "55%": { transform: "scale(1.25)" },
            "100%": { transform: "scale(1)" },
        },
        cursor: "pointer",
        fill: "inherit",
        fillOpacity: 0.36,
        ":hover": {
            animation: "breathe 1.5s infinite ease-out",
            fill: "blue",
        },
    })
);

const MapControl = styled("div")(
    ({ theme }) => ({
        position: "absolute",
        right: 0,
        bottom: 0,
        margin: theme.spacing(1),
        width: 48,
    })
);

const MapContainer = styled((props) => (
    <Card elevation={5} {...props} />
))(({ theme }) => ({
    margin: theme.spacing(2, 0),
    position: "relative",
    backgroundColor: "#aadaff",
    fill: "#fefefe",
    cursor: "grab",
    [theme.breakpoints.up("lg")]: {
        width: "48%",
    },
}));

const MapMarker = styled(Marker)(
    ({theme}) => ({
        fill: "#d53a9d",
        "&:hover": {
            "& text": {
                display: "block",
            },
        },
    })
);

const MapMarkerText = styled((props) => (
    <text x={6} y={6} {...props} />
))(({theme}) => ({
        display: "none",
        fill: "#555",
        fontSize: "0.5rem",
        textTransform: "capitalize",
        fontFamily: "montserrat",
        fontWeight: 800,
}));

export default function MapCard(props) {
    const { continent, markers, onClick } = props;
    const [position, setPosition] = useState(null);
    const [geography, setGeography] = useState(null);

    useEffect(() => {
        setPosition({ coordinates: regionSettings[continent].center, zoom: regionSettings[continent].zoom });
        const fetchMap = () => {
            return fetch(`https://raw.githubusercontent.com/deldersveld/topojson/master/continents/${continent}.json`)
                .then((resp) => resp.json())
                .then((data) => setGeography(data));
        };
        fetchMap();
        return () => setGeography(null);
    }, [continent]);

    const handleMoveEnd = (pos) => setPosition(pos);

    return (
        <MapContainer>
            <ComposableMap style={{
                marginBottom: -6,
                height: "100%",
            }}>
                {geography && position && (
                    <ZoomableGroup
                        zoom={position.zoom}
                        center={position.coordinates}
                        maxZoom={maxZoom}
                        onMoveEnd={handleMoveEnd}
                        // prevent mouse wheel scrolling
                        filterZoomEvent={(evt) => (evt.type === "wheel" ? false : true)}
                    >
                        <Geographies geography={geography}>
                            {({ geographies }) => (
                                geographies.map(geo => {
                                    return <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        style={{
                                            default: { outline: "none", fill: "inherit" },
                                            hover: { outline: "none", fill: "inherit" },
                                        }}
                                    />
                                })
                            )}
                        </Geographies>
                        {markers && markers.map(({ city, coordinates, size }, idx) => (
                            <MapMarker
                                key={idx}
                                coordinates={coordinates}
                                onClick={onClick}
                                id={`marker-${idx}`}
                            >
                                {position.zoom && <PoPMarker r={Math.max(3 * size / position.zoom || 1, 1)} />}
                                <MapMarkerText>{city}</MapMarkerText>
                            </MapMarker>
                        ))}
                    </ZoomableGroup>
                )}
            </ComposableMap>
            <CardBackdrop open={!(markers && geography)} />
            {position && (
                <MapControl>
                    <IconButton
                        size="large"
                        onClick={() => setPosition((prev) => ({
                            ...prev, zoom: prev.zoom + 2,
                        }))}
                        disabled={position.zoom >= maxZoom}
                    >
                        <AddIcon />
                    </IconButton>
                    <IconButton
                        size="large"
                        onClick={() => setPosition((prev) => ({
                            ...prev, zoom: prev.zoom - 2,
                        }))}
                        disabled={position.zoom <= minZoom}
                    >
                        <RemoveIcon />
                    </IconButton>
                </MapControl>
            )}
        </MapContainer>
    );
};